import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import communityService, {
  type CommunityDashboardResponse,
  type CommunityDashboardUpcomingEvent,
  type ChartDataPoint,
} from '@/services/communityService';
import { getWalletData } from '@/services/walletService';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';
import { hostMabarStatistikPath } from '@/utils/hostPaths';

// Utility helper for currency formatting
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Utility helper for numbers
const formatNumber = (value: number) => {
  return new Intl.NumberFormat('id-ID').format(value);
};

// Date helper
const shortDayMonth = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
      'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
    ];
    const fullDays = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    return {
      day: days[d.getDay()],
      fullDay: fullDays[d.getDay()],
      date: d.getDate(),
      month: months[d.getMonth()],
      year: d.getFullYear(),
    };
  } catch {
    return { day: '-', fullDay: '-', date: 0, month: '-', year: 2026 };
  }
};

// Standard Default Fallbacks
const DEFAULT_DASHBOARD: CommunityDashboardResponse = {
  total_member: 0,
  event_revenue: 0,
  event_30_ended: 0,
  event_30_total: 0,
  event_mabar_total: 0,
  sparing_upcoming: 0,
  upcoming_activities: [],
  recent_activities: [],
};

// Full Page Skeleton Component for Community Home
function CommunityHomeSkeleton() {
  return (
    <div className="min-h-screen  p-4 md:p-6 space-y-6 animate-pulse text-[#0E1726]">
      {/* Mobile / Top Header Skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-6 w-44 bg-gray-200 rounded-lg" />
          <div className="h-3.5 w-28 bg-gray-200 rounded-md" />
        </div>
        <div className="h-9 w-32 bg-gray-200 rounded-full" />
      </div>

      {/* Wallet Banner Card Skeleton */}
      <div className="h-32 w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl" />

      {/* Content Layout Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Main Column */}
        <div className="md:col-span-8 space-y-6">
          {/* Featured Event Card Skeleton */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-5 w-36 bg-gray-200 rounded-md" />
              <div className="h-4 w-20 bg-gray-200 rounded-md" />
            </div>
            <div className="h-44 w-full bg-gray-200 rounded-2xl" />
          </div>

          {/* Stats Grid Skeleton */}
          <div className="space-y-3">
            <div className="h-5 w-40 bg-gray-200 rounded-md" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="h-28 bg-white border border-gray-150 rounded-2xl p-3 space-y-2" />
              <div className="h-28 bg-white border border-gray-150 rounded-2xl p-3 space-y-2" />
              <div className="h-28 bg-white border border-gray-150 rounded-2xl p-3 space-y-2" />
              <div className="h-28 bg-white border border-gray-150 rounded-2xl p-3 space-y-2" />
            </div>
          </div>

          {/* Chart Skeleton */}
          <div className="h-64 w-full bg-white border border-gray-150 rounded-2xl p-5 space-y-4" />
        </div>

        {/* Desktop Sidebar Skeleton */}
        <div className="hidden md:block md:col-span-4 space-y-6">
          <div className="h-48 bg-white border border-gray-150 rounded-2xl p-5 space-y-3" />
          <div className="h-64 bg-white border border-gray-150 rounded-2xl p-5 space-y-3" />
        </div>
      </div>
    </div>
  );
}

// Interactive Trend Line Chart Component (Using Real API Data)
function GrowthTrendChart({ dashboardData }: { dashboardData: CommunityDashboardResponse | null }) {
  const [activeTab, setActiveTab] = useState<'anggota' | 'pendapatan'>('anggota');
  const [period, setPeriod] = useState<'Per Bulan' | 'Per Minggu'>('Per Bulan');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine current chart data from real API dashboardData
  let rawData: ChartDataPoint[] = [];
  if (period === 'Per Bulan') {
    rawData = activeTab === 'anggota'
      ? dashboardData?.trend_monthly_members || []
      : dashboardData?.trend_monthly_revenue || [];
  } else {
    rawData = activeTab === 'anggota'
      ? dashboardData?.trend_weekly_members || []
      : dashboardData?.trend_weekly_revenue || [];
  }

  // Fallback data if backend hasn't returned points yet
  if (!rawData || rawData.length === 0) {
    const fallbackLabels = period === 'Per Bulan'
      ? ['Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov']
      : ['M1', 'M2', 'M3', 'M4', 'M5', 'M6'];
    rawData = fallbackLabels.map((l) => ({ label: l, value: 0 }));
  }

  const maxValue = Math.max(...rawData.map((d) => d.value), 1);

  // SVG dimensions
  const svgWidth = 320;
  const svgHeight = 120;
  const paddingX = 20;
  const paddingY = 20;

  // Calculate points
  const points = rawData.map((d, index) => {
    const x = paddingX + (index / Math.max(rawData.length - 1, 1)) * (svgWidth - paddingX * 2);
    const y = svgHeight - paddingY - (d.value / maxValue) * (svgHeight - paddingY * 2);
    return { x, y, label: d.label, value: d.value };
  });

  // Create SVG path string
  const pathD = points.reduce((acc, point, i) => {
    return i === 0 ? `M ${point.x} ${point.y}` : `${acc} L ${point.x} ${point.y}`;
  }, '');

  // Create SVG area fill path
  const areaD = `${pathD} L ${points[points.length - 1].x} ${svgHeight} L ${points[0].x} ${svgHeight} Z`;

  const activePoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <div className="bg-white rounded-2xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
      {/* Header & Period Dropdown */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-black text-[#0E1726]">Perkembangan</h3>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className="h-8 px-3 rounded-full border border-[#E0E6ED] bg-white text-xs font-bold text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 cursor-pointer shadow-2xs transition-colors"
          >
            <span>{period}</span>
            <Icon
              icon="ph:caret-down-bold"
              className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-xl shadow-lg border border-gray-150 z-30 py-1 text-gray-800">
              <button
                type="button"
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-orange-50 flex items-center justify-between cursor-pointer ${
                  period === 'Per Bulan' ? 'text-[#FC480D] font-bold bg-orange-50/50' : 'text-gray-700'
                }`}
                onClick={() => {
                  setPeriod('Per Bulan');
                  setIsDropdownOpen(false);
                }}
              >
                <span>Per Bulan</span>
                {period === 'Per Bulan' && <Icon icon="ph:check-bold" className="w-3.5 h-3.5 text-[#FC480D]" />}
              </button>

              <button
                type="button"
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-orange-50 flex items-center justify-between cursor-pointer ${
                  period === 'Per Minggu' ? 'text-[#FC480D] font-bold bg-orange-50/50' : 'text-gray-700'
                }`}
                onClick={() => {
                  setPeriod('Per Minggu');
                  setIsDropdownOpen(false);
                }}
              >
                <span>Per Minggu</span>
                {period === 'Per Minggu' && <Icon icon="ph:check-bold" className="w-3.5 h-3.5 text-[#FC480D]" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="bg-[#F5F6F7] p-1 rounded-xl flex items-center gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('anggota')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'anggota'
              ? 'bg-white text-[#0E1726] shadow-xs'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          Anggota
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('pendapatan')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'pendapatan'
              ? 'bg-white text-[#0E1726] shadow-xs'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          Pendapatan
        </button>
      </div>

      {/* Interactive Line Chart Canvas */}
      <div className="relative pt-4 pb-2">
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-32 overflow-visible">
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FC480D" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#FC480D" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Area under curve */}
          <path d={areaD} fill="url(#chartGradient)" />

          {/* Line curve */}
          <path
            d={pathD}
            fill="none"
            stroke="#FC480D"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Vertical guide line on HOVER ONLY */}
          {activePoint && (
            <line
              x1={activePoint.x}
              y1={10}
              x2={activePoint.x}
              y2={svgHeight - 15}
              stroke="#FC480D"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
          )}

          {/* Data Points */}
          {points.map((p, idx) => (
            <circle
              key={idx}
              cx={p.x}
              cy={p.y}
              r={hoveredIndex === idx ? 6 : 4}
              className="cursor-pointer transition-all"
              fill={hoveredIndex === idx ? '#FC480D' : '#fff'}
              stroke="#FC480D"
              strokeWidth="2.5"
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          ))}
        </svg>

        {/* Floating Tooltip Card (HOVER ONLY) */}
        {activePoint && (
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white px-3.5 py-1.5 rounded-xl border border-gray-150 shadow-md text-center pointer-events-none z-10 transition-all"
            style={{
              left: `${(activePoint.x / svgWidth) * 100}%`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <span className="text-[10px] font-bold text-gray-400 block leading-tight">
              {activePoint.label}
            </span>
            <span className="text-xs font-black text-[#0E1726] block leading-tight">
              {activeTab === 'anggota'
                ? `${activePoint.value} Anggota`
                : formatCurrency(activePoint.value)}
            </span>
          </div>
        )}

        {/* X-Axis Month/Week Labels */}
        <div className="flex items-center justify-between text-xs font-semibold text-gray-400 px-2 mt-2">
          {points.map((p, idx) => (
            <button
              key={idx}
              type="button"
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`cursor-pointer transition-colors ${
                hoveredIndex === idx ? 'text-[#FC480D] font-bold' : 'hover:text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper to robustly parse event timestamps with timezone offsets or fallback date strings
function parseTargetTime(target?: string): number {
  if (!target) return NaN;
  let d = new Date(target).getTime();
  if (!isNaN(d)) return d;

  // Try cleaning space before offset e.g. "2026-09-17 23:00:00 +0800"
  let cleaned = target.trim().replace(/\s+([+-]\d{2}:?\d{2})$/, '$1');
  d = new Date(cleaned).getTime();
  if (!isNaN(d)) return d;

  // Try replacing date-time space with T
  cleaned = cleaned.replace(' ', 'T');
  d = new Date(cleaned).getTime();
  if (!isNaN(d)) return d;

  return NaN;
}

// Countdown hook for live event timer
function useCountdown(targetIsoDate?: string) {
  const calculateTimeLeft = (target?: string) => {
    if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const targetTime = parseTargetTime(target);
    if (isNaN(targetTime)) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const now = Date.now();
    const diff = targetTime - now;

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds };
  };

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetIsoDate));

  useEffect(() => {
    if (!targetIsoDate) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    setTimeLeft(calculateTimeLeft(targetIsoDate));

    const interval = setInterval(() => {
      const updated = calculateTimeLeft(targetIsoDate);
      setTimeLeft(updated);
      if (updated.days === 0 && updated.hours === 0 && updated.minutes === 0 && updated.seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetIsoDate]);

  return timeLeft;
}

const CommunityHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<CommunityDashboardResponse | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch Dashboard Data
  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [res, walletRes] = await Promise.all([
        communityService.getCommunityDashboard(),
        getWalletData().catch(() => null),
      ]);
      setData(res);
      if (walletRes && typeof walletRes.balance === 'number') {
        setWalletBalance(walletRes.balance);
      }
    } catch (err) {
      console.error('Failed to load community dashboard data:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const d = data ?? DEFAULT_DASHBOARD;
  const communityName = user?.community_user?.name || user?.name || 'Garuda Muda';
  const cityLocation = 'Yogyakarta';
  const communityAvatar = user?.community_user?.avatar || user?.avatar;

  // Flatten and process all upcoming activities into a chronological event list
  const nowMs = Date.now();
  const upcomingEvents: CommunityDashboardUpcomingEvent[] = (d.upcoming_activities || [])
    .flatMap((day) => {
      return (day.events || []).map((ev) => ({
        ...ev,
        event_date: day.date,
      }));
    })
    .filter((ev) => {
      const startTimeMs = parseTargetTime(
        ev.start_time?.includes('T') || ev.start_time?.includes('-') || (ev.start_time && ev.start_time.length > 8)
          ? ev.start_time
          : `${ev.event_date}T${ev.start_time || '00:00:00'}`
      );
      const endTimeMs = ev.end_time
        ? parseTargetTime(
            ev.end_time.includes('T') || ev.end_time.includes('-') || ev.end_time.length > 8
              ? ev.end_time
              : `${ev.event_date}T${ev.end_time || '00:00:00'}`
          )
        : !isNaN(startTimeMs)
        ? startTimeMs + 2 * 60 * 60 * 1000
        : NaN;

      const validEndTime = !isNaN(endTimeMs) ? endTimeMs : !isNaN(startTimeMs) ? startTimeMs : 0;
      return validEndTime >= nowMs;
    })
    .sort((a, b) => {
      const timeA = parseTargetTime(
        a.start_time?.includes('T') || a.start_time?.includes('-') || (a.start_time && a.start_time.length > 8)
          ? a.start_time
          : `${a.event_date}T${a.start_time || '00:00:00'}`
      );
      const timeB = parseTargetTime(
        b.start_time?.includes('T') || b.start_time?.includes('-') || (b.start_time && b.start_time.length > 8)
          ? b.start_time
          : `${b.event_date}T${b.start_time || '00:00:00'}`
      );
      return (isNaN(timeA) ? 0 : timeA) - (isNaN(timeB) ? 0 : timeB);
    });

  // Featured Event is the upcoming event with the closest start time
  const featuredEvent = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  const remainingEvents = upcomingEvents.length > 1 ? upcomingEvents.slice(1) : [];

  // Countdown timer & metadata for featured event
  const countdownTarget = featuredEvent
    ? (featuredEvent.start_time?.includes('T') || featuredEvent.start_time?.includes('-') || (featuredEvent.start_time && featuredEvent.start_time.length > 8))
      ? featuredEvent.start_time
      : `${featuredEvent.event_date}T${featuredEvent.start_time || '00:00:00'}`
    : undefined;
  const countdown = useCountdown(countdownTarget);
  const featuredDateInfo = featuredEvent
    ? shortDayMonth(featuredEvent.start_time || featuredEvent.event_date)
    : { day: '-', fullDay: '-', date: 0, month: '-', year: 2026 };
  const featuredTime = featuredEvent
    ? formatLocalEventTimeRange(featuredEvent.start_time, featuredEvent.end_time)
    : '';

  const quotaFilled = featuredEvent?.quota_filled ?? 0;
  const quotaTotal = featuredEvent?.quota_total ?? 0;
  const slotsRemaining = Math.max(quotaTotal - quotaFilled, 0);
  const quotaPercentage = quotaTotal > 0 ? Math.min(Math.round((quotaFilled / quotaTotal) * 100), 100) : 0;

  // 📊 Stat Cards Computed Metrics
  const revenueAmount = d.event_revenue ?? 0;
  const totalMemberCount = d.total_member ?? 0;

  const growthPct = d.revenue_growth_percent ?? 0;
  const growthSign = growthPct >= 0 ? '+' : '';
  const lastMonth = d.last_month_name || 'bulan lalu';
  const revenueBadgeText = `${growthSign}${Math.round(growthPct)}% dari ${lastMonth}`;

  const joinedThisMonth = d.member_joined_this_month ?? 0;
  const memberBadgeText = `+${joinedThisMonth} bulan ini`;

  const endedEventsCount = (d.events_ended_total && d.events_ended_total > 0)
    ? d.events_ended_total
    : (d.event_30_ended && d.event_30_ended > 0)
    ? d.event_30_ended
    : (d.event_mabar_total ?? 0);
  const eventsThisMonth = d.events_this_month ?? 0;
  const eventsBadgeText = `${eventsThisMonth} bulan ini`;

  const totalVisitsCount = d.total_visits ?? 0;
  const avgVisits = d.avg_visits_per_event ?? 0;
  const visitsBadgeText = `Rata-rata ${avgVisits}/acara`;

  if (loading) {
    return <CommunityHomeSkeleton />;
  }

  return (
    <div className="min-h-screen  text-[#0E1726]">
      {/* ========================================================================= */}
      {/* 📱 MOBILE FIRST VIEW (< md) */}
      {/* ========================================================================= */}
      <div className="block md:hidden pb-28">

        {/* MAIN BODY CONTAINER */}
        <div className="px-4 pt-4 space-y-5">
          {/* 💳 WALLET BANNER CARD */}
          <div
            onClick={() => navigate('/host/wallet')}
            className="rounded-2xl p-5 bg-gradient-to-r from-[#FF8552] to-[#FC480D] text-white shadow-md relative overflow-hidden space-y-3.5 cursor-pointer active:scale-[0.99] transition-transform"
          >
            <div className="absolute -right-8 -top-12 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />

            <div className="flex items-end justify-between gap-3 relative z-10">
              <div>
                <div className="flex items-center gap-1 text-xs font-semibold text-white/85">
                  <span>Saldo Komunitas</span>
                  <Icon icon="ph:caret-right-bold" className="w-3 h-3 text-white/80" />
                </div>
                <div className="text-2xl font-black tracking-tight mt-0.5 leading-tight">
                  {formatCurrency(walletBalance ?? d.event_revenue_total ?? 0)}
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/host/wallet');
                }}
                className="h-10 px-4 rounded-full bg-[#1F1F1F] hover:bg-black text-white text-xs font-bold flex items-center gap-1.5 transition-colors shadow-2xs cursor-pointer shrink-0"
              >
                <Icon icon="ph:arrow-line-down-bold" className="w-4 h-4" />
                <span>Tarik</span>
              </button>
            </div>

            <div className="pt-3 border-t border-white/25 flex items-center gap-2 text-xs font-medium text-white/90 relative z-10">
              <Icon icon="ph:trend-up-bold" className="w-4 h-4 text-white" />
              <span>Pendapatan bulan ini {formatCurrency(d.event_revenue || 0)}</span>
            </div>
          </div>

          {/* ⚽ EVENT BERIKUTNYA SECTION */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-[#0E1726]">Event Berikutnya</h2>
              <button
                type="button"
                onClick={() => navigate('/host/mabar')}
                className="text-xs font-bold text-[#FC480D] hover:underline cursor-pointer"
              >
                Lihat Semua
              </button>
            </div>

            {featuredEvent ? (
              /* FEATURED EVENT HERO CARD */
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FF7A45] to-[#FC480D] text-white shadow-md relative overflow-hidden space-y-4">
                <div className="absolute -right-10 -top-14 w-44 h-44 rounded-full bg-white/10 pointer-events-none" />

                {/* Category & Status Row */}
                <div className="flex items-center justify-between gap-2.5 relative z-10 !mt-0">
                  <span className="px-3 py-1.5 rounded-full bg-white/[0.22] text-[11px] font-bold tracking-[0.06em] text-white uppercase">
                    {featuredEvent.sport_category || 'MINI SOCCER'}
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-[#1F1F1F]/32 text-xs font-semibold text-white whitespace-nowrap">
                    {featuredEvent.is_paid !== false ? 'Sudah dibayar' : 'Belum bayar'}
                  </span>
                </div>

                {/* Title & Info */}
                <div className="space-y-2 relative z-10">
                  <h3 className="text-xl font-black leading-snug tracking-tight">
                    {featuredEvent.name}
                  </h3>

                  <div className="space-y-1 text-xs text-white/90 font-medium">
                    <div className="flex items-center gap-2">
                      <Icon icon="ph:calendar-dots-bold" className="w-4 h-4 text-white shrink-0" />
                      <span>
                        {featuredDateInfo.fullDay}, {featuredDateInfo.date} {featuredDateInfo.month} {featuredDateInfo.year} · {featuredTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-white shrink-0" />
                      <span>{featuredEvent.venue_name || 'Gor Senayan Yogyakarta'}</span>
                    </div>
                  </div>
                </div>

                {/* Live Countdown Box */}
                <div className="bg-white/18 backdrop-blur-md rounded-xl p-2.5 flex items-center justify-between text-center border border-white/20 relative z-10">
                  <div className="flex-1">
                    <div className="text-lg font-black leading-none">{String(countdown.days).padStart(2, '0')}</div>
                    <div className="text-[10px] font-medium text-white/85 mt-0.5">Hari</div>
                  </div>
                  <span className="text-white/70 font-black text-sm">:</span>
                  <div className="flex-1">
                    <div className="text-lg font-black leading-none">{String(countdown.hours).padStart(2, '0')}</div>
                    <div className="text-[10px] font-medium text-white/85 mt-0.5">Jam</div>
                  </div>
                  <span className="text-white/70 font-black text-sm">:</span>
                  <div className="flex-1">
                    <div className="text-lg font-black leading-none">{String(countdown.minutes).padStart(2, '0')}</div>
                    <div className="text-[10px] font-medium text-white/85 mt-0.5">Menit</div>
                  </div>
                  <span className="text-white/70 font-black text-sm">:</span>
                  <div className="flex-1">
                    <div className="text-lg font-black leading-none">{String(countdown.seconds).padStart(2, '0')}</div>
                    <div className="text-[10px] font-medium text-white/85 mt-0.5">Detik</div>
                  </div>
                </div>

                {/* Slot Progress Bar */}
                <div className="space-y-1.5 relative z-10">
                  <div className="flex items-center justify-between text-xs font-semibold text-white">
                    <span>{quotaFilled} dari {quotaTotal} kuota terisi</span>
                    <span className="text-white/85">Sisa {slotsRemaining} slot</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/30 overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${quotaPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3 pt-1 relative z-10">
                  <button
                    type="button"
                    onClick={() => navigate(`/host/mabar/${featuredEvent.slug}/dashboard`)}
                    className="h-11 rounded-full bg-white text-[#FC480D] hover:bg-orange-50 font-bold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:chart-bar-bold" className="w-4 h-4" />
                    <span>Analitik</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(hostMabarStatistikPath(featuredEvent.slug, featuredEvent.sport_category))
                    }
                    className="h-11 rounded-full bg-white/[0.22] border border-white/60 text-white hover:bg-white/35 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:chart-pie-slice-bold" className="w-4 h-4" />
                    <span>Statistik</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-[#E0E6ED] text-center space-y-3 shadow-2xs">
                <div className="w-12 h-12 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto">
                  <Icon icon="ph:calendar-blank-bold" className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-black text-[#0E1726] text-base">Belum Ada Event Mendatang</h3>
                  <p className="text-xs text-gray-400 font-medium">Event terdekat akan otomatis tampil di sini.</p>
                </div>
              </div>
            )}

            {/* REMAINING UPCOMING EVENTS LIST */}
            {remainingEvents.length > 0 && (
              <div className="space-y-2.5 pt-1">
                {remainingEvents.map((ev) => {
                  const evDateInfo = shortDayMonth(ev.event_date);
                  const evFilled = ev.quota_filled ?? 0;
                  const evTotal = ev.quota_total ?? 0;
                  const evPct = evTotal > 0 ? Math.min(Math.round((evFilled / evTotal) * 100), 100) : 0;

                  return (
                    <div
                      key={ev.id}
                      onClick={() => navigate(`/host/mabar/${ev.slug}/dashboard`)}
                      className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs flex items-start gap-3.5 cursor-pointer hover:border-[#FC480D] transition-colors"
                    >
                      {/* Date Badge Box */}
                      <div className="w-14 h-14 rounded-xl  flex flex-col items-center justify-center shrink-0">
                        <span className="text-[11px] font-bold text-[#FC480D] uppercase">
                          {evDateInfo.month}
                        </span>
                        <span className="text-xl font-black text-[#0E1726] leading-none">
                          {evDateInfo.date}
                        </span>
                      </div>

                      {/* Event Body */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <h4 className="font-extrabold text-[#0E1726] text-sm truncate leading-snug">
                          {ev.name}
                        </h4>
                        <div className="flex items-center gap-1 text-xs text-gray-400 font-medium truncate">
                          <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{ev.venue_name || 'Gor Senayan'}</span>
                        </div>

                        <div className="flex items-center gap-2 pt-0.5">
                          <span className="px-2 py-0.5 rounded bg-orange-50 text-[#FC480D] text-[11px] font-bold">
                            {ev.sport_category || 'Futsal'}
                          </span>
                          <span className="text-xs font-semibold text-gray-500">
                            {evFilled}/{evTotal} Bergabung
                          </span>
                        </div>

                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1">
                          <div
                            className="h-full bg-[#FC480D] rounded-full"
                            style={{ width: `${evPct}%` }}
                          />
                        </div>
                      </div>

                      {/* Kelola Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/host/mabar/${ev.slug}/dashboard`);
                        }}
                        className="h-8 px-3 rounded-full border border-[#FC480D] text-[#FC480D] hover:bg-orange-50 text-xs font-bold flex items-center gap-1 shrink-0 cursor-pointer mt-1"
                      >
                        <span>Kelola</span>
                        <Icon icon="ph:caret-right-bold" className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 📊 STATISTIK KOMUNITAS SECTION */}
          <div className="space-y-3 pt-1">
            <h2 className="text-lg font-black text-[#0E1726]">Statistik Komunitas</h2>

            <div className="grid grid-cols-2 gap-3">
              {/* Card 1: Pendapatan bulan ini */}
              <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs space-y-2">
                <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:wallet-bold" className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-400 block">Pendapatan bulan ini</span>
                  <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                    {formatCurrency(revenueAmount)}
                  </span>
                </div>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${growthPct >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                  <span>{revenueBadgeText}</span>
                </div>
              </div>

              {/* Card 2: Total anggota */}
              <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs space-y-2">
                <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:users-three-bold" className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-400 block">Total anggota</span>
                  <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                    {formatNumber(totalMemberCount)}
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">
                  <span>{memberBadgeText}</span>
                </div>
              </div>

              {/* Card 3: Acara terselenggara */}
              <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs space-y-2">
                <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:calendar-check-bold" className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-400 block">Acara terselenggara</span>
                  <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                    {formatNumber(endedEventsCount)}
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold">
                  <span>{eventsBadgeText}</span>
                </div>
              </div>

              {/* Card 4: Total kunjungan */}
              <div className="bg-white p-3.5 rounded-2xl border border-[#E0E6ED] shadow-2xs space-y-2">
                <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:chart-line-up-bold" className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-400 block">Total kunjungan</span>
                  <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                    {formatNumber(totalVisitsCount)}
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold">
                  <span>{visitsBadgeText}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 📈 PERKEMBANGAN TREND CHART SECTION */}
          <div className="pt-1">
            <GrowthTrendChart dashboardData={d} />
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 💻 DESKTOP VIEW (>= md) */}
      {/* ========================================================================= */}
      <div className="hidden md:block max-w-6xl mx-auto space-y-6 pb-12 pt-4">
        {/* DESKTOP HEADER & SALDO BAR */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs flex flex-col min-[1080px]:flex-row min-[1080px]:items-center justify-between gap-5 min-[1080px]:gap-6">
          <div className="flex items-center gap-4 min-w-0">
            {communityAvatar ? (
              <img
                src={communityAvatar}
                alt={communityName}
                className="w-14 h-14 rounded-full object-cover border-2 border-orange-200 bg-white shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#FC480D] text-white font-black flex items-center justify-center text-xl shrink-0">
                {communityName.substring(0, 2).toUpperCase()}
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-[#0E1726]">Halo, {communityName}</h1>
                <span className="px-3 py-1 rounded-full bg-orange-50 text-[#FC480D] text-xs font-bold flex items-center gap-1 shrink-0">
                  <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5" />
                  <span>{cityLocation}</span>
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1 font-medium">
                Selamat datang di Dashboard Komunitas Sporto. Pantau event mabar & perkembangan komunitasmu.
              </p>
            </div>
          </div>

          {/* Wallet Saldo Box */}
          <div className="bg-gradient-to-r from-[#FF8552] to-[#FC480D] p-5 rounded-2xl text-white flex items-center justify-between gap-4 sm:gap-6 w-full min-[1080px]:w-auto min-[1080px]:min-w-[320px] shadow-sm shrink-0">
            <div className="min-w-0">
              <span className="text-xs font-semibold text-white/85 block">Saldo Komunitas</span>
              <span className="text-2xl font-black tracking-tight block leading-tight mt-0.5 truncate">
                {formatCurrency(walletBalance ?? d.event_revenue_total ?? 0)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/host/wallet')}
              className="h-10 px-5 rounded-full bg-[#1F1F1F] hover:bg-black text-white text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer shrink-0"
            >
              <Icon icon="ph:arrow-line-down-bold" className="w-4 h-4" />
              <span>Tarik Saldo</span>
            </button>
          </div>
        </div>

        {/* 2-COLUMN DASHBOARD GRID */}
        <div className="grid grid-cols-12 gap-6 items-start">
          {/* LEFT COLUMN: Featured Event & Upcoming List (7 cols) */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            {featuredEvent ? (
              /* FEATURED HERO EVENT CARD */
              <div className="rounded-3xl p-6 bg-gradient-to-br from-[#FF7A45] to-[#FC480D] text-white shadow-md relative overflow-hidden space-y-5">
                <div className="flex items-center justify-between gap-2">
                  <span className="px-3.5 py-1.5 rounded-full bg-white/[0.22] text-xs font-black uppercase tracking-wider text-white">
                    {featuredEvent.sport_category || 'MINI SOCCER'}
                  </span>
                  <span className="px-3.5 py-1.5 rounded-full bg-[#1F1F1F]/35 backdrop-blur-xs text-xs font-semibold text-white">
                    {featuredEvent.is_paid !== false ? 'Sudah dibayar' : 'Belum bayar'}
                  </span>
                </div>

                <div className="space-y-2">
                  <h2 className="text-2xl font-black leading-snug tracking-tight">
                    {featuredEvent.name}
                  </h2>
                  <div className="space-y-1 text-xs text-white/90 font-medium">
                    <div className="flex items-center gap-2">
                      <Icon icon="ph:calendar-dots-bold" className="w-4 h-4 text-white shrink-0" />
                      <span>
                        {featuredDateInfo.fullDay}, {featuredDateInfo.date} {featuredDateInfo.month} {featuredDateInfo.year} · {featuredTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-white shrink-0" />
                      <span>{featuredEvent.venue_name || 'Gor Senayan Yogyakarta'}</span>
                    </div>
                  </div>
                </div>

                {/* Desktop Countdown Box */}
                <div className="bg-white/18 backdrop-blur-md rounded-2xl p-4 flex items-center justify-between text-center border border-white/20">
                  <div className="flex-1">
                    <div className="text-2xl font-black leading-none">{String(countdown.days).padStart(2, '0')}</div>
                    <div className="text-xs font-medium text-white/85 mt-1">Hari</div>
                  </div>
                  <span className="text-white/70 font-black text-lg">:</span>
                  <div className="flex-1">
                    <div className="text-2xl font-black leading-none">{String(countdown.hours).padStart(2, '0')}</div>
                    <div className="text-xs font-medium text-white/85 mt-1">Jam</div>
                  </div>
                  <span className="text-white/70 font-black text-lg">:</span>
                  <div className="flex-1">
                    <div className="text-2xl font-black leading-none">{String(countdown.minutes).padStart(2, '0')}</div>
                    <div className="text-xs font-medium text-white/85 mt-1">Menit</div>
                  </div>
                  <span className="text-white/70 font-black text-lg">:</span>
                  <div className="flex-1">
                    <div className="text-2xl font-black leading-none">{String(countdown.seconds).padStart(2, '0')}</div>
                    <div className="text-xs font-medium text-white/85 mt-1">Detik</div>
                  </div>
                </div>

                {/* Slot Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-semibold text-white">
                    <span>{quotaFilled} dari {quotaTotal} kuota terisi</span>
                    <span className="text-white/85">Sisa {slotsRemaining} slot</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-white/30 overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${quotaPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4 pt-1">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(hostMabarStatistikPath(featuredEvent.slug, featuredEvent.sport_category))
                    }
                    className="h-12 rounded-full bg-white text-[#FC480D] hover:bg-orange-50 font-extrabold text-xs flex items-center justify-center gap-2 shadow-2xs transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:chart-bar-bold" className="w-4 h-4" />
                    <span>Statistik Event</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/host/mabar/${featuredEvent.slug}/dashboard`)}
                    className="h-12 rounded-full bg-white/[0.22] border border-white/60 text-white hover:bg-white/35 font-extrabold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:chart-pie-slice-bold" className="w-4 h-4" />
                    <span>Statistik Match</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-8 border border-[#E0E6ED] text-center space-y-3 shadow-2xs">
                <div className="w-14 h-14 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto">
                  <Icon icon="ph:calendar-blank-bold" className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-black text-[#0E1726] text-lg">Belum Ada Event Mendatang</h3>
                  <p className="text-xs text-gray-400 font-medium">Event terdekat akan otomatis tampil sebagai highlight di sini.</p>
                </div>
              </div>
            )}

            {/* UPCOMING EVENTS LIST */}
            {remainingEvents.length > 0 && (
              <div className="bg-white rounded-3xl p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-extrabold text-[#0E1726]">Event Berikutnya</h3>
                  <button
                    type="button"
                    onClick={() => navigate('/host/mabar')}
                    className="text-xs font-bold text-[#FC480D] hover:underline cursor-pointer"
                  >
                    Lihat Semua
                  </button>
                </div>

                <div className="space-y-3">
                  {remainingEvents.map((ev) => {
                    const evDateInfo = shortDayMonth(ev.event_date);
                    const evFilled = ev.quota_filled ?? 0;
                    const evTotal = ev.quota_total ?? 0;
                    const evPct = evTotal > 0 ? Math.min(Math.round((evFilled / evTotal) * 100), 100) : 0;

                    return (
                      <div
                        key={ev.id}
                        onClick={() => navigate(`/host/mabar/${ev.slug}/dashboard`)}
                        className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-200 hover:border-[#FC480D] transition-all flex items-center justify-between gap-4 cursor-pointer group"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex flex-col items-center justify-center shrink-0 shadow-2xs">
                            <span className="text-[11px] font-bold text-[#FC480D] uppercase">
                              {evDateInfo.month}
                            </span>
                            <span className="text-xl font-black text-[#0E1726] leading-none">
                              {evDateInfo.date}
                            </span>
                          </div>

                          <div className="space-y-1 min-w-0">
                            <h4 className="font-extrabold text-[#0E1726] text-sm truncate group-hover:text-[#FC480D] transition-colors">
                              {ev.name}
                            </h4>
                            <div className="flex items-center gap-1 text-xs text-gray-400 font-medium truncate">
                              <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{ev.venue_name || 'Gor Senayan'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2.5 py-0.5 rounded bg-orange-50 text-[#FC480D] text-[11px] font-bold">
                                {ev.sport_category || 'Futsal'}
                              </span>
                              <span className="text-xs font-semibold text-gray-500">
                                {evFilled}/{evTotal} Bergabung
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 shrink-0">
                          <div className="w-24 space-y-1 hidden sm:block">
                            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div className="h-full bg-[#FC480D] rounded-full" style={{ width: `${evPct}%` }} />
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/host/mabar/${ev.slug}/dashboard`);
                            }}
                            className="h-9 px-4 rounded-full border border-[#FC480D] text-[#FC480D] hover:bg-orange-50 text-xs font-bold flex items-center gap-1 cursor-pointer"
                          >
                            <span>Kelola</span>
                            <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Community Stats & Trend Chart (5 cols) */}
          <div className="col-span-12 lg:col-span-5 space-y-6">
            {/* COMMUNITY STATS GRID */}
            <div className="bg-white rounded-3xl p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
              <h3 className="text-lg font-extrabold text-[#0E1726]">Statistik Komunitas</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 space-y-2">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:wallet-bold" className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-400 block">Pendapatan bulan ini</span>
                    <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                      {formatCurrency(revenueAmount)}
                    </span>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${growthPct >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                    {revenueBadgeText}
                  </span>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 space-y-2">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:users-three-bold" className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-400 block">Total anggota</span>
                    <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                      {formatNumber(totalMemberCount)}
                    </span>
                  </div>
                  <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">
                    {memberBadgeText}
                  </span>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 space-y-2">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:calendar-check-bold" className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-400 block">Acara terselenggara</span>
                    <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                      {formatNumber(endedEventsCount)}
                    </span>
                  </div>
                  <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold">
                    {eventsBadgeText}
                  </span>
                </div>

                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-gray-100 space-y-2">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center">
                    <Icon icon="ph:chart-line-up-bold" className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-400 block">Total kunjungan</span>
                    <span className="text-lg font-black text-[#0E1726] block leading-tight mt-0.5">
                      {formatNumber(totalVisitsCount)}
                    </span>
                  </div>
                  <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold">
                    {visitsBadgeText}
                  </span>
                </div>
              </div>
            </div>

            {/* INTERACTIVE TREND CHART */}
            <GrowthTrendChart dashboardData={d} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityHomePage;

